import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { contains } from './dom.js';
import { isApple } from './platform.js';

/**
 * Returns `true` if `event` has been fired within a React Portal element.
 */

function isPortalEvent(event) {
  return !contains(event.currentTarget, event.target);
}
/**
 * Returns `true` if `event.target` and `event.currentTarget` are the same.
 */

function isSelfTarget(event) {
  return event.target === event.currentTarget;
}
/**
 * Checks whether the user event is triggering a page navigation in a new tab.
 */

function isOpeningInNewTab(event) {
  var element = event.currentTarget;
  if (!element) return false;
  var isAppleDevice = isApple();
  if (isAppleDevice && !event.metaKey) return false;
  if (!isAppleDevice && !event.ctrlKey) return false;
  var tagName = element.tagName.toLowerCase();
  if (tagName === "a") return true;
  if (tagName === "button" && element.type === "submit") return true;
  if (tagName === "input" && element.type === "submit") return true;
  return false;
}
/**
 * Checks whether the user event is triggering a download.
 */

function isDownloading(event) {
  var element = event.currentTarget;
  if (!element) return false;
  var tagName = element.tagName.toLowerCase();
  if (!event.altKey) return false;
  if (tagName === "a") return true;
  if (tagName === "button" && element.type === "submit") return true;
  if (tagName === "input" && element.type === "submit") return true;
  return false;
}
/**
 * Creates and dispatches an event.
 * @example
 * fireEvent(document.getElementById("id"), "blur", {
 *   bubbles: true,
 *   cancelable: true,
 * });
 */

function fireEvent(element, type, eventInit) {
  var event = new Event(type, eventInit);
  return element.dispatchEvent(event);
}
/**
 * Creates and dispatches a blur event.
 * @example
 * fireBlurEvent(document.getElementById("id"));
 */

function fireBlurEvent(element, eventInit) {
  var event = new FocusEvent("blur", eventInit);
  var defaultAllowed = element.dispatchEvent(event);
  var bubbleInit = _objectSpread(_objectSpread({}, eventInit), {}, {
    bubbles: true
  });
  element.dispatchEvent(new FocusEvent("focusout", bubbleInit));
  return defaultAllowed;
}
/**
 * Creates and dispatches a focus event.
 * @example
 * fireFocusEvent(document.getElementById("id"));
 */

function fireFocusEvent(element, eventInit) {
  var event = new FocusEvent("focus", eventInit);
  var defaultAllowed = element.dispatchEvent(event);
  var bubbleInit = _objectSpread(_objectSpread({}, eventInit), {}, {
    bubbles: true
  });
  element.dispatchEvent(new FocusEvent("focusin", bubbleInit));
  return defaultAllowed;
}
/**
 * Creates and dispatches a keyboard event.
 * @example
 * fireKeyboardEvent(document.getElementById("id"), "keydown", {
 *   key: "ArrowDown",
 *   shiftKey: true,
 * });
 */

function fireKeyboardEvent(element, type, eventInit) {
  var event = new KeyboardEvent(type, eventInit);
  return element.dispatchEvent(event);
}
/**
 * Creates and dispatches a click event.
 * @example
 * fireClickEvent(document.getElementById("id"));
 */

function fireClickEvent(element, eventInit) {
  var event = new MouseEvent("click", eventInit);
  return element.dispatchEvent(event);
}
/**
 * Checks whether the focus/blur event is happening from/to outside of the
 * container element.
 * @example
 * const element = document.getElementById("id");
 * element.addEventListener("blur", (event) => {
 *   if (isFocusEventOutside(event)) {
 *     // ...
 *   }
 * });
 */

function isFocusEventOutside(event, container) {
  var containerElement = container || event.currentTarget;
  var relatedTarget = event.relatedTarget;
  return !relatedTarget || !contains(containerElement, relatedTarget);
}
/**
 * Runs a callback on the next animation frame, but before a certain event.
 */

function queueBeforeEvent(element, type, callback) {
  var raf = requestAnimationFrame(function () {
    element.removeEventListener(type, callImmediately, true);
    callback();
  });
  var callImmediately = function callImmediately() {
    cancelAnimationFrame(raf);
    callback();
  }; // By listening to the event in the capture phase, we make sure the callback
  // is fired before the respective React events.

  element.addEventListener(type, callImmediately, {
    once: true,
    capture: true
  });
  return raf;
}
/**
 * Adds a global event listener, including on child frames.
 */

function addGlobalEventListener(type, listener, options, scope) {
  if (scope === void 0) {
    scope = window;
  }

  // Prevent errors from "sandbox" frames.
  try {
    scope.document.addEventListener(type, listener, options);
  } catch (e) {}
  var listeners = [];
  for (var i = 0; i < ((_scope$frames = scope.frames) == null ? void 0 : _scope$frames.length); i += 1) {
    var _scope$frames;
    var frameWindow = scope.frames[i];
    if (frameWindow) {
      listeners.push(addGlobalEventListener(type, listener, options, frameWindow));
    }
  }
  var removeEventListener = function removeEventListener() {
    try {
      scope.document.removeEventListener(type, listener, options);
    } catch (e) {}
    listeners.forEach(function (listener) {
      return listener();
    });
  };
  return removeEventListener;
}
export { addGlobalEventListener, fireBlurEvent, fireClickEvent, fireEvent, fireFocusEvent, fireKeyboardEvent, isDownloading, isFocusEventOutside, isOpeningInNewTab, isPortalEvent, isSelfTarget, queueBeforeEvent };